/**
 * Minimal RFC 4180 CSV reader/writer.
 *
 * Written by hand rather than pulled in as a dependency — the import flow only
 * needs quoted fields, escaped quotes, embedded newlines and a delimiter guess,
 * and the repo has no CSV library today.
 */

export interface CsvTable {
    /** Header row, with blanks filled in as `Column N` so every column is addressable. */
    headers: string[]
    /** Data rows, always padded to `headers.length`. */
    rows: string[][]
    delimiter: string
}

const CANDIDATE_DELIMITERS = [',', ';', '\t', '|']

/** Reads the first physical line, ignoring newlines that live inside quotes. */
const firstLogicalLine = (text: string): string => {
    let inQuotes = false
    for (let i = 0; i < text.length; i++) {
        const ch = text[i]
        if (ch === '"') {
            if (inQuotes && text[i + 1] === '"') i++
            else inQuotes = !inQuotes
        } else if (!inQuotes && (ch === '\n' || ch === '\r')) {
            return text.slice(0, i)
        }
    }
    return text
}

/** Picks whichever candidate delimiter appears most often in the header line. */
export const detectDelimiter = (text: string): string => {
    const line = firstLogicalLine(text.replace(/^﻿/, ''))
    let best = ','
    let bestCount = 0
    for (const candidate of CANDIDATE_DELIMITERS) {
        let count = 0
        let inQuotes = false
        for (let i = 0; i < line.length; i++) {
            const ch = line[i]
            if (ch === '"') {
                if (inQuotes && line[i + 1] === '"') i++
                else inQuotes = !inQuotes
            } else if (!inQuotes && ch === candidate) {
                count++
            }
        }
        if (count > bestCount) {
            best = candidate
            bestCount = count
        }
    }
    return best
}

/**
 * Parses `input` into a header row plus data rows. Values are returned exactly
 * as they appeared (minus the quoting) — trimming is the caller's job.
 */
export const parseCsv = (input: string, delimiter?: string): CsvTable => {
    const text = (input ?? '').replace(/^﻿/, '')
    const sep = delimiter || detectDelimiter(text)

    const records: string[][] = []
    let record: string[] = []
    let field = ''
    let inQuotes = false
    let sawAnyChar = false

    const endField = () => {
        record.push(field)
        field = ''
    }
    const endRecord = () => {
        endField()
        records.push(record)
        record = []
        sawAnyChar = false
    }

    for (let i = 0; i < text.length; i++) {
        const ch = text[i]

        if (inQuotes) {
            if (ch === '"') {
                if (text[i + 1] === '"') {
                    field += '"'
                    i++
                } else {
                    inQuotes = false
                }
            } else {
                field += ch
            }
            continue
        }

        if (ch === '"' && field === '') {
            inQuotes = true
            sawAnyChar = true
        } else if (ch === sep) {
            sawAnyChar = true
            endField()
        } else if (ch === '\r') {
            if (text[i + 1] === '\n') i++
            endRecord()
        } else if (ch === '\n') {
            endRecord()
        } else {
            sawAnyChar = true
            field += ch
        }
    }
    if (field !== '' || record.length > 0 || sawAnyChar) endRecord()

    // Drop rows that are entirely empty (trailing newlines, blank separator lines).
    const meaningful = records.filter((r) => r.some((c) => c.trim() !== ''))
    if (meaningful.length === 0) {
        return { headers: [], rows: [], delimiter: sep }
    }

    const rawHeaders = meaningful[0]
    const headers = rawHeaders.map(
        (h, i) => h.trim() || `Column ${i + 1}`
    )

    const rows = meaningful.slice(1).map((r) => {
        const padded = r.slice(0, headers.length)
        while (padded.length < headers.length) padded.push('')
        return padded
    })

    return { headers, rows, delimiter: sep }
}

/** Quotes a single value for CSV output. */
const escapeCsvValue = (value: string): string => {
    const text = value ?? ''
    return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

/** Serializes rows back to CSV text (CRLF line endings, for Excel's benefit). */
export const toCsv = (rows: string[][]): string =>
    rows.map((row) => row.map(escapeCsvValue).join(',')).join('\r\n')
